/**
 * PQL field catalog — builds the parser's vocabulary from the resolved filter
 * field defs (config/filterFields.ts `resolveFilterFieldGroups` output).
 *
 * Name resolution order: exact key → alias → case-insensitive key →
 * case-insensitive label → `meta.<key>` ⇄ `meta:<key>`. Unknown names get a
 * Levenshtein nearest-neighbor suggestion.
 */

import type { PqlCatalog, PqlContext, PqlFieldSpec } from './types.ts';

/**
 * Friendly aliases → canonical registry keys. Only applied when the target
 * field actually exists in the supplied defs (so a workspace that hides a
 * field doesn't get a ghost alias).
 */
export const FIELD_ALIASES: Record<string, string> = {
  status: 'exit_status',
  category: 'itemCategory',
  subjecttype: 'metaCategory',
  tag: 'tags',
  created: 'createdAt',
  updated: 'updatedAt',
  cycle: 'cycleId',
  track: 'trackId',
  session: 'sessionId',
  collection: 'inCollection',
  pool: 'inItemPool',
  stale: 'isStale',
  // The item list keys altitude as `altitude`; the Smart Collection vocabulary
  // (smartCollectionFields.ts) as `altitudeLevel`. Exact keys win, so this
  // only fires where `altitude` itself is absent — statements written on the
  // item list re-parse unchanged as a collection rule.
  altitude: 'altitudeLevel',
};

/** Minimal input shape — matches FilterFieldDef without importing it at runtime. */
export interface PqlFieldDefInput {
  field: string;
  label: string;
  type: PqlFieldSpec['type'];
  options?: PqlFieldSpec['options'];
}

function levenshtein(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  let prev = Array.from({ length: n + 1 }, (_, j) => j);
  for (let i = 1; i <= m; i++) {
    const curr = [i];
    for (let j = 1; j <= n; j++) {
      curr[j] = Math.min(
        prev[j] + 1,
        curr[j - 1] + 1,
        prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1),
      );
    }
    prev = curr;
  }
  return prev[n];
}

export function buildCatalog(defs: PqlFieldDefInput[], context: PqlContext = {}): PqlCatalog {
  const fields: PqlFieldSpec[] = defs.map((d) => {
    const spec: PqlFieldSpec = { key: d.field, label: d.label, type: d.type };
    if (d.options !== undefined) spec.options = d.options;
    return spec;
  });

  const byKey = new Map<string, PqlFieldSpec>();
  const byLowerKey = new Map<string, PqlFieldSpec>();
  const byLowerLabel = new Map<string, PqlFieldSpec>();
  for (const f of fields) {
    byKey.set(f.key, f);
    if (!byLowerKey.has(f.key.toLowerCase())) byLowerKey.set(f.key.toLowerCase(), f);
    if (!byLowerLabel.has(f.label.toLowerCase())) byLowerLabel.set(f.label.toLowerCase(), f);
  }

  const resolveField = (name: string): PqlFieldSpec | undefined => {
    const exact = byKey.get(name);
    if (exact) return exact;
    // Statement form `meta.someKey` ⇄ canonical `meta:someKey`
    if (name.toLowerCase().startsWith('meta.')) {
      return byKey.get(`meta:${name.slice(5)}`);
    }
    const lower = name.toLowerCase();
    const alias = FIELD_ALIASES[lower.replace(/[\s_-]/g, '')];
    if (alias && byKey.has(alias)) return byKey.get(alias);
    return byLowerKey.get(lower) ?? byLowerLabel.get(lower);
  };

  const suggestField = (name: string): string | undefined => {
    const lower = name.toLowerCase();
    let best: string | undefined;
    let bestDist = Infinity;
    const candidates = [
      ...fields.map((f) => f.key),
      ...Object.keys(FIELD_ALIASES).filter((a) => byKey.has(FIELD_ALIASES[a])),
    ];
    for (const candidate of candidates) {
      const dist = levenshtein(lower, candidate.toLowerCase());
      if (dist < bestDist) {
        bestDist = dist;
        best = candidate;
      }
    }
    // Only suggest when plausibly a typo (≤40% of the name edited, minimum 3).
    const threshold = Math.max(3, Math.floor(name.length * 0.4));
    return bestDist <= threshold ? best : undefined;
  };

  return { fields, context, resolveField, suggestField };
}
